import React, { CSSProperties, useCallback, useEffect } from "react";
import { ClickCursor, DragCursor, PointCursor, TypeCursor } from "./VirtualCursorSvg";
import { ShadowDomContainer } from "./ShadowDomContainer";
import styles from "./VirtualCursor.module.css";
import { throttle } from "lodash-es";
import { DragStepAttributes } from "@common/models";

type Point = { x: number; y: number };

let lastOutlinedTarget = undefined as Element | undefined;
const clearLastOutlinedTarget = () => lastOutlinedTarget?.classList.remove(styles.pyneCursorFocus);

export function VirtualCursor({
  id = "pyne-cursor",
  target: _target,
  visible,
  click,
  scrollTargetIntoView,
  direction,
  startPosition = { x: window.innerWidth / 2, y: window.innerHeight / 2 },
  shiftOffset = { top: "-9px", left: "-14px" },
  travelDuration,
}: {
  id?: string;
  target?: Element | null;
  visible: boolean;
  click?: "move" | "point" | "type" | "drag";
  forcusTarget?: boolean;
  scrollTargetIntoView?: boolean;
  direction?: DragStepAttributes["direction"];
  startPosition?: { x: number; y: number };
  shiftOffset?: { top: string; left: string };
  travelDuration?: number;
}) {
  const clickWaveRef = React.useRef<HTMLDivElement>(null);
  const cursorImgRef = React.useRef<SVGSVGElement>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);

  const [state, setState] = React.useState({
    target: undefined as HTMLElement | undefined | null,
    transitionTarget: startPosition,
    transitionDuration: 0,
    targetAnimationPlayed: false,
  });

  const moveTo = useCallback(
    (targetPosition: Point) => {
      if (scrollTargetIntoView) state.target?.scrollIntoView({ block: "center", inline: "center" });

      const currentPosition = { x: state.transitionTarget.x, y: state.transitionTarget.y };
      const duration = travelDuration ?? planAnimation(currentPosition, targetPosition);
      setState((s) => ({
        ...s,
        transitionTarget: { ...targetPosition },
        transitionDuration: state.targetAnimationPlayed ? 0 : duration,
      }));
    },
    [
      state.target,
      state.targetAnimationPlayed,
      scrollTargetIntoView,
      state.transitionTarget.x,
      state.transitionTarget.y,
    ]
  );

  const getTargetPostion = useCallback(() => {
    if (!state.target) return { x: 0, y: 0 };
    const rect = state.target.getBoundingClientRect();
    const halfWidth = click === "type" ? 32 : rect.width / 2;
    return { x: rect.left + halfWidth, y: rect.top + rect.height / 2 };
  }, [state.target, click]);

  useEffect(() => {
    if (_target !== state.target) {
      setState((s) => ({ ...s, target: _target as HTMLElement, targetAnimationPlayed: false }));
      clearLastOutlinedTarget();
    }
  }, [_target, state.target]);

  useEffect(() => {
    if (state.target) {
      const { x, y } = getTargetPostion();
      moveTo({ x, y });
    }
  }, [click, getTargetPostion, moveTo, state.target]);

  useEffect(() => {
    return () => {
      clearLastOutlinedTarget();
    };
  }, [visible]);

  useEffect(() => {
    const move = throttle(() => {
      const { x, y } = getTargetPostion();
      moveTo({ x, y });
    }, 300);

    if (state.target) {
      window.addEventListener("scroll", move, true);
    }

    return () => window.removeEventListener("scroll", move, true);
  }, [moveTo, getTargetPostion, state.target]);

  function playClickAnimation() {
    if (!cursorImgRef.current || !state.target || state.targetAnimationPlayed) return;
    setState((s) => ({ ...s, targetAnimationPlayed: true }));

    const pauseBeforeClick = 100;

    if (click === "move" && clickWaveRef.current) {
      cursorImgRef.current
        .animate(
          [
            {
              transition: `transform 75ms linear`,
            },
            {
              scale: 0.9,
              filter: "brightness(0.8)",
            },
            {
              filter: "brightness(1)",
            },
          ],
          {
            duration: 400,
            easing: "ease-out",
            fill: "forwards",
            delay: pauseBeforeClick,
          }
        )
        .play();
      clickWaveRef.current
        .animate(
          [
            { transform: "scale(0)", opacity: 1 },
            { transform: "scale(1)", opacity: 0 },
          ],
          {
            duration: 1000,
            easing: "ease-out",
            fill: "forwards",
            delay: pauseBeforeClick + 50,
          }
        )
        .play();
    } else if (click === "point") {
      const anim = cursorImgRef.current.animate([{ offsetDistance: 0 }, { offsetDistance: "100%" }], {
        duration: 2000,
        easing: "linear",
        iterations: 2,
        fill: "forwards",
      });
      anim.onfinish = () => {
        if (cursorImgRef.current) {
          cursorImgRef.current.style.offsetPath = "none";
        }
      };
    } else if (click === "drag") {
      const transition = getDragTransition(direction!);
      const dragAnimation: KeyframeAnimationOptions = {
        duration: 700,
        easing: "ease-in-out",
        iterations: 2,
      };
      cursorImgRef.current.animate(transition, dragAnimation).onfinish = () => {
        if (cursorImgRef.current) {
          cursorImgRef.current.style.transform = "none";
        }
      };
      state.target.animate(transition, dragAnimation);
    }
  }

  const shouldHide = !visible || (state.transitionTarget.x === 0 && state.transitionTarget.y === 0);
  const pointerStyle: CSSProperties = {
    offsetPath: "circle(40px)",
    offsetRotate: "0deg",
    offsetRotation: "0deg",
  };
  const defaultStyle: CSSProperties = {
    top: shiftOffset.top,
    left: shiftOffset.left,
    position: "fixed",
    fill: "var(--pyne-primary)",
    zIndex: "var(--pyne-layer-cursor)",
    pointerEvents: "none",
    opacity: shouldHide ? 0 : 1,
  };
  const transformStyles: CSSProperties = {
    transform: `translate(${state.transitionTarget.x}px, ${state.transitionTarget.y}px)`,
    transitionDuration: `${state.transitionDuration}ms`,
  };

  return (
    <ShadowDomContainer
      id={id}
      containerRef={containerRef}
      style={{
        ...defaultStyle,
        ...transformStyles,
      }}
      onTransitionEnd={() => {
        if (click) {
          playClickAnimation();
        }
      }}
    >
      {click === "point" && <PointCursor style={{ ...pointerStyle, ...defaultStyle }} svgRef={cursorImgRef} />}
      {click === "move" && (
        <>
          <ClickCursor style={defaultStyle} svgRef={cursorImgRef} />
          <div className={styles.clickWave} style={transformStyles} ref={clickWaveRef} />
        </>
      )}

      {/* Had to pass fill explicitly here otherwise it wasn't changing the color */}
      {click === "type" && <TypeCursor style={defaultStyle} svgRef={cursorImgRef} fill="var(--pyne-primary)" />}
      {click === "drag" && (
        <>
          <DragCursor style={defaultStyle} svgRef={cursorImgRef} />
        </>
      )}
    </ShadowDomContainer>
  );
}

function planAnimation(currentPosition: Point, targetPosition: Point) {
  // shorter animation for small distances
  const distance = getDistance(currentPosition, targetPosition);
  const duration = distance < 100 ? 200 : 500;

  return duration;
}

function getDistance(p1: Point, p2: Point) {
  return Math.sqrt((p1.x - p2.x) ** 2 + (p1.y - p2.y) ** 2);
}

function getDragTransition(direction: DragStepAttributes["direction"]): Keyframe[] {
  switch (direction) {
    case "up":
      return [{ transform: `translateY(0)` }, { transform: `translateY(-28px)` }, { transform: `translateY(0)` }];
    case "down":
      return [{ transform: "translateY(0)" }, { transform: "translateY(28px)" }, { transform: "translateY(0)" }];
    case "left":
      return [{ transform: "translateX(0)" }, { transform: "translateX(-28px)" }, { transform: "translateX(0)" }];
    default:
      return [{ transform: "translateX(0)" }, { transform: "translateX(28px)" }, { transform: "translateX(0)" }];
  }
}
